import { Tooltip } from "./Tooltip";

const PHASES = [
  { key: "processing", label: "Processing" },
  { key: "ready", label: "Ready" },
  { key: "awaiting", label: "Awaiting" },
  { key: "completed", label: "Completed" },
] as const;

type PhaseKey = (typeof PHASES)[number]["key"];

const CANCELLED_STATES = new Set(["cancelled", "to_cancel", "cancelling"]);
const MOVING_STATES = new Set(["to_move", "moving"]);
const READY_STATES = new Set(["sent_invite", "signed_fee_agreement"]);

function isSessionPast(date?: string | null, endTime?: string | null): boolean {
  if (!date || !endTime) return false;
  try {
    const sessionEnd = new Date(`${date}T${endTime}`);
    return sessionEnd < new Date();
  } catch {
    return false;
  }
}

function resolvePhase(
  state: string,
  date?: string | null,
  endTime?: string | null,
): { phase: PhaseKey; cancelled: boolean; moving: boolean } {
  if (CANCELLED_STATES.has(state)) {
    return { phase: "processing", cancelled: true, moving: false };
  }
  if (MOVING_STATES.has(state)) {
    return { phase: "ready", cancelled: false, moving: true };
  }
  if (state === "closed") {
    return { phase: "completed", cancelled: false, moving: false };
  }
  if (READY_STATES.has(state)) {
    if (isSessionPast(date, endTime)) {
      return { phase: "awaiting", cancelled: false, moving: false };
    }
    return { phase: "ready", cancelled: false, moving: false };
  }
  return { phase: "processing", cancelled: false, moving: false };
}

interface BookingProgressBarProps {
  state: string;
  availabilityDate?: string | null;
  availabilityEndTime?: string | null;
  /** Compact = bar only, no labels. Default: false. */
  compact?: boolean;
  className?: string;
}

export function BookingProgressBar({
  state,
  availabilityDate,
  availabilityEndTime,
  compact = false,
  className = "",
}: BookingProgressBarProps) {
  const { phase, cancelled, moving } = resolvePhase(
    state,
    availabilityDate,
    availabilityEndTime,
  );
  const currentPhaseIndex = PHASES.findIndex((p) => p.key === phase);
  const isFullyComplete = phase === "completed";

  function segmentClass(i: number): string {
    if (isFullyComplete) return "bg-sage";
    if (i < currentPhaseIndex) return "bg-sage";
    if (i === currentPhaseIndex) {
      if (cancelled) return "bg-rust";
      if (moving) return "bg-spark";
      return "bg-tide";
    }
    return "bg-ink/10";
  }

  function labelClass(i: number): string {
    if (isFullyComplete || i < currentPhaseIndex) return "text-sage";
    if (i === currentPhaseIndex) {
      if (cancelled) return "text-rust";
      if (moving) return "text-spark";
      return "text-tide";
    }
    return "text-ink-5";
  }

  // Visual bar size
  const barHeight = compact ? "h-1" : "h-1.5";
  const gap = compact ? "gap-1" : "gap-1.5";
  // Hit area — invisible padding around each segment so tooltips
  // are easy to land on. Negative margin pulls it back so the
  // surrounding row height doesn't change.
  const hitArea = compact ? "py-2.5 -my-2.5" : "py-2 -my-2";

  return (
    <div
      className={`${compact ? "w-[104px]" : "w-full"} ${className}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={PHASES.length}
      aria-valuenow={isFullyComplete ? PHASES.length : currentPhaseIndex + 1}
      aria-label={`Booking progress: ${PHASES[currentPhaseIndex]?.label ?? "Unknown"}${
        cancelled ? " (cancelled)" : moving ? " (moving)" : ""
      }`}
    >
      <div className={`flex items-center ${gap}`}>
        {PHASES.map((p, i) => (
          <Tooltip
            key={p.key}
            label={p.label}
            className={`flex-1 items-center cursor-default ${hitArea}`}
          >
            <span
              className={`block w-full ${barHeight} rounded-full ${segmentClass(i)} transition-colors`}
            />
          </Tooltip>
        ))}
      </div>
      {!compact && (
        <div className={`flex ${gap} mt-2`}>
          {PHASES.map((p, i) => (
            <div
              key={p.key}
              className={`flex-1 text-center text-[10px] uppercase tracking-[0.06em] font-medium ${labelClass(i)}`}
            >
              {p.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
